import { Routes } from '@/constants/Routes';
import { ResolvedRewardsProgram } from '@/pages/CustomerDashboard/interfaces';
import { tierGraphics } from '@/pages/CustomerDashboard/TierImage';
import { ResolvedTier } from '@api/data/RewardsProgramData';
import { ApiFarmerRewards } from '@api/interfaces/ApiFarmerRewards';
import { ApiRewardsTier } from '@api/interfaces/ApiRewardsTier';
import { ApiRewardsTierSegment } from '@api/interfaces/ApiRewardsTierSegment';
import { useLocation, useNavigate } from 'react-router-dom';


export const getRemainingPctToHurdle = (
  segment: ApiRewardsTierSegment,
  totalPerSegment: ApiFarmerRewards['totalPerSegment'],
  tier: ApiRewardsTier,
) => {
  const total = totalPerSegment[segment.categoryId].total;
  const segmentToHurdle = tier.segments!.find(({ categoryId }) => (
    categoryId === segment.categoryId
  ))!;
  return (segmentToHurdle.minimumHurdle - total) / segmentToHurdle.minimumHurdle;
};

export const getHasTierGraphics = (tierNames: string[]) => {
  return tierNames.every((tierName) => (
    Object.keys(tierGraphics).includes(tierName)
  ));
};

const getTier = (tiers: ResolvedTier[], rank: number | null): ResolvedTier | null => {
  return tiers.find(({ rank: tierRank }) => tierRank === rank) ?? null;
};

export const getProgramInfo = (
  program?: ResolvedRewardsProgram,
  rewards?: ApiFarmerRewards,
  getDefaults: boolean = false,
) => {
  if (getDefaults || !program || !rewards) {
    return {
      areAllCategoriesHurdled: false,
      currentTier: {} as ResolvedTier,
      hasTierGraphics: false,
      isReachedFinalTier: false,
      isTierSpendHurdled: false,
      nextTier: {} as ResolvedTier,
      numCategoriesToHurdle: 0,
      relevantTier: {} as ResolvedTier,
      spendToHurdle: 0,
    };
  }
  const currentTier = getTier(program.tiers, rewards.rankCurrent);
  const earnedTier = getTier(program.tiers, rewards.rankEarned);
  const nextTier = (rewards.rankEarned ?? 0) < program.tiers.length - 1
    ? getTier(program.tiers, (rewards.rankEarned ?? -1) + 1)
    : null;
  const relevantTier = (nextTier ?? earnedTier)!;
  const isReachedFinalTier = !nextTier;
  const earnedSegmentsHurdled = relevantTier?.segments.filter((segment) => (
    rewards.totalPerSegment[segment.categoryId].total >= segment.minimumHurdle
  )).map(({ categoryId }) => categoryId) ?? [];
  const numCategoriesToHurdle = Math.max(
    relevantTier.minimumSegments - earnedSegmentsHurdled.length,
    0,
  );
  const spendToHurdle = Math.max(relevantTier.minimumDollars - rewards.qualifyingTotalSpend, 0);
  const isTierSpendHurdled = spendToHurdle <= 0;
  const areAllCategoriesHurdled = numCategoriesToHurdle === 0;
  const hasTierGraphics = getHasTierGraphics(program.tiers.map(({ name }) => name));

  return {
    areAllCategoriesHurdled,
    currentTier,
    hasTierGraphics,
    isReachedFinalTier,
    isTierSpendHurdled,
    nextTier,
    numCategoriesToHurdle,
    relevantTier,
    spendToHurdle,
  };
};

export const useDashboardNavigate = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const defaultToAltRoutes: Partial<Record<Routes, Routes>> = {
    [Routes.CUSTOMER_DASHBOARD]: Routes.CUSTOMER_DASHBOARD_ALT,
    [Routes.CUSTOMER_DASHBOARD_BENEFITS]: Routes.CUSTOMER_DASHBOARD_BENEFITS_ALT,
    [Routes.CUSTOMER_DASHBOARD_BOOKING_DETAIL]: Routes.CUSTOMER_DASHBOARD_BOOKING_DETAIL_ALT,
    [Routes.CUSTOMER_DASHBOARD_BOOKINGS]: Routes.CUSTOMER_DASHBOARD_BOOKINGS_ALT,
    [Routes.CUSTOMER_DASHBOARD_FAQ]: Routes.CUSTOMER_DASHBOARD_FAQ_ALT,
    [Routes.CUSTOMER_DASHBOARD_HOW_IT_WORKS]: Routes.CUSTOMER_DASHBOARD_HOW_IT_WORKS_ALT,
    [Routes.CUSTOMER_DASHBOARD_POINTS_ACTIVITY]: Routes.CUSTOMER_DASHBOARD_POINTS_ACTIVITY_ALT,
    [Routes.CUSTOMER_DASHBOARD_PROMOTIONS]: Routes.CUSTOMER_DASHBOARD_PROMOTIONS_ALT,
    [Routes.CUSTOMER_DASHBOARD_WALLET]: Routes.CUSTOMER_DASHBOARD_WALLET_ALT,
  };

  return (
    route: Routes,
    accessToken?: string,
    pathParams?: Record<string, string>,
    state?: Record<string, unknown>,
  ) => {
    const isAlt = location.pathname.includes('/db/');
    let path = isAlt
      ? defaultToAltRoutes[route]!.replace(':accessToken', accessToken!)
      : `${route}?accessToken=${accessToken}`;
    if (pathParams) {
      for (const [key, value] of Object.entries(pathParams)) {
        path = path.replace(`:${key}`, value);
      }
    }
    const program = new URLSearchParams(location.search).get('program');
    if (program) {
      path = `${path}${path.includes('?') ? '&' : '?'}program=${program}`;
    }
    navigate(path, { state });
  };
};

